import type { NavLink, SiteDocument } from "./types";
import { pagePath, realPageHref, siteHeaderLinks } from "./utils";
export type HeaderMenuItem = NavLink & { children: NavLink[] };
type MenuSite = Pick<SiteDocument, "pages" | "navigation" | "header">;
export const pageHeaderId = (id: string) => `page-header-${id}`;

/** Accept legacy custom IDs pointing to an auto page link, without duplicating it. */
export function canonicalHeaderId(site: MenuSite, reference: string | null | undefined): string | null {
  if (!reference) return null;
  const page = site.pages.find(page => pageHeaderId(page.id) === reference || page.id === reference);
  if (page) return pageHeaderId(page.id);
  const link = site.navigation.find(link => link.id === reference);
  if (!link) return reference;
  const target = site.pages.find(page => pagePath(page) === realPageHref(site, link.href));
  return target ? pageHeaderId(target.id) : link.id;
}
function allItems(site: MenuSite): NavLink[] {
  const paths = new Set(site.pages.map(pagePath));
  return [...site.pages.map(page => ({ id: pageHeaderId(page.id), label: page.navigationLabel || page.title, href: pagePath(page), parentId: page.navigationParentId, icon: page.navigationIcon })), ...site.navigation.filter(link => !paths.has(realPageHref(site, link.href)))];
}
/** Separate semantic validation keeps siteSchema an object for existing pick/omit users. */
export function headerMenuErrors(site: MenuSite): string[] {
  const items = allItems(site), nodes = new Map(items.map(item => [item.id, item]));
  if (nodes.size !== items.length) return ["Header menu IDs must be unique across pages and custom links."];
  if (items.some(item => site.navigation.some(link => link.id === item.id && site.pages.some(page => page.id === link.id)))) return ["Custom menu IDs must not collide with page IDs."];
  for (const item of items) {
    const parentId = canonicalHeaderId(site, item.parentId); if (!parentId) continue;
    if (!item.href) return ["Group-only menu labels must stay at the top level."];
    const parent = nodes.get(parentId);
    if (!parent) return ["A dropdown parent must reference a page or menu group on this website."];
    if (parentId === item.id) return ["A menu item cannot be its own dropdown parent."];
    if (canonicalHeaderId(site, parent.parentId)) return ["Dropdown menus support one submenu level; cycles and nested dropdowns are not allowed."];
  }
  return [];
}
/** Always bounded: hidden/missing parents promote their children to top level. */
export function siteHeaderMenu(site: MenuSite): HeaderMenuItem[] {
  const links = siteHeaderLinks(site), visible = new Map(links.map(link => [link.id, link]));
  const validParent = (link: NavLink) => {
    const id = canonicalHeaderId(site, link.parentId), parent = id ? visible.get(id) : undefined;
    return parent && parent.id !== link.id && !canonicalHeaderId(site, parent.parentId) ? parent.id : null;
  };
  return links.filter(link => !validParent(link)).map(link => ({ ...link, children: links.filter(child => validParent(child) === link.id) })).filter(link => Boolean(link.href) || link.children.length > 0);
}
export function setHeaderParent(site: SiteDocument, itemId: string, parentId: string | null): SiteDocument {
  const page = site.pages.find(page => pageHeaderId(page.id) === itemId);
  const next = page ? { ...site, pages: site.pages.map(item => item.id === page.id ? { ...item, navigationParentId: parentId } : item) } : { ...site, navigation: site.navigation.map(item => item.id === itemId ? { ...item, parentId } : item) };
  if (headerMenuErrors(next).length) return site;
  return next;
}
export function clearHeaderParent(site: SiteDocument, parentId: string): SiteDocument {
  const belongs = (value?: string | null) => canonicalHeaderId(site, value) === parentId;
  return { ...site, pages: site.pages.map(page => belongs(page.navigationParentId) ? { ...page, navigationParentId: null } : page), navigation: site.navigation.map(link => belongs(link.parentId) ? { ...link, parentId: null } : link) };
}
export function removeHeaderLink(site: SiteDocument, id: string): SiteDocument {
  const cleared = clearHeaderParent(site, canonicalHeaderId(site, id) || id);
  return { ...cleared, navigation: cleared.navigation.filter(link => link.id !== id), header: { ...cleared.header, linkOrder: cleared.header?.linkOrder?.filter(value => value !== id) } };
}
